/**
 * 申請の詳細 `/hr/wf-requests/:id`。申請時のスナップショット・承認の履歴・タスク・社外依頼。
 *
 * 詳細テンプレート(`DetailPage`): 見出し(種別 + 対象者 + 状態)、操作(承認 / 却下 / 取り下げ /
 * 削除。権限と状態で出し分け)、要約、タブ(概要 / タスク / 社外依頼 / 履歴。`?tab=`)。
 * 操作は一覧と同じ `wfActions.tsx`。
 */
import { Alert, Button, Card, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Link, useNavigate, useParams } from "react-router-dom";

import type { WfExternalRequest, WfTask } from "../../lib/api";
import { jst } from "../../lib/format";
import { DetailPage, KeyValueList, Section, Steps, type KvItem } from "../../ui";
import { kindLabel, requestStatusLabel, statusLabel, taskModeLabel } from "./constants";
import { errorText } from "./hooks";
import { ApproveReject, CompleteTask, DeleteClosed, RerunTask, SlaTag, Withdraw } from "./wfActions";
import { externalStatusTag, requestStatusTag, taskLoansCell, taskNameCell, taskStatusCell } from "./wfBits";
import { useRequest } from "./wfHooks";
import { approvalRoute } from "./wfRoute";

export function RequestDetailPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const query = useRequest(requestId ? Number(requestId) : undefined);
  const req = query.data;

  if (query.isError)
    return (
      <Alert
        type="error"
        showIcon
        message="申請を取得できなかった"
        description={errorText(query.error)}
        action={
          <Link to="/hr/requests/list">
            <Button size="small">申請一覧へ</Button>
          </Link>
        }
      />
    );
  if (!req) return <Card loading size="small" />;

  const payload = req.payload ?? {};
  const fields = payload.fields ?? {};
  const steps = payload.steps ?? [];
  const returnLoans = payload.return_loans ?? [];
  const changes = payload.changes ?? {};

  const contentItems: KvItem[] = [
    ...Object.entries(fields).map(([k, v]) => ({ label: k, value: v })),
    { label: "SaaS", value: (payload.saas ?? []).join(" / ") },
    { label: "工程", value: steps.map((s) => `${s.section ? `${s.section}/` : ""}${s.name}`).join(" / "), wide: true },
    {
      label: "返却する貸与品",
      value: returnLoans.map((l) => `${l.item}${l.asset_no ? `(${l.asset_no})` : ""}`).join(" / "),
      wide: true,
    },
    {
      label: "マスタへの反映",
      value: Object.keys(changes).length ? <code style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(changes)}</code> : "",
      wide: true,
    },
    // 申請の組み込み備考は廃止(2026-10-19)。備考を持つ古い申請だけ出す
    ...(payload.note ? [{ label: "備考", value: String(payload.note), wide: true }] : []),
  ];

  const taskColumns: ColumnsType<WfTask> = [
    { title: "#", dataIndex: "id", width: 110, render: (v: number) => <span className="ui-num">{v}</span> },
    {
      title: "内容",
      width: 320,
      render: (_: unknown, t) => (
        <div>
          {taskNameCell(t)}
          {taskLoansCell(t)}
        </div>
      ),
    },
    { title: "方式", width: 160, render: (_: unknown, t) => taskModeLabel(t.mode) },
    { title: "担当", dataIndex: "owner", width: 160 },
    { title: "状態", width: 210, render: (_: unknown, t) => taskStatusCell(t) },
    {
      title: "完了・記録",
      ellipsis: true,
      onCell: (row) => ({ title: [row.completed_at ? `${jst(row.completed_at)} / ${row.completed_by}` : "", row.result].filter(Boolean).join("\n") }),
      width: 360,
      render: (_: unknown, t) => (
        <div>
          {t.completed_at && <div>{`${jst(t.completed_at)} / ${t.completed_by}`}</div>}
          {t.result && <div className="ui-cell-sub">{t.result}</div>}
        </div>
      ),
    },
    {
      title: "操作",
      width: 180,
      render: (_: unknown, t) => (
        <Space size={4} wrap>
          <CompleteTask task={t} />
          <RerunTask task={t} />
        </Space>
      ),
    },
  ];

  const extColumns: ColumnsType<WfExternalRequest> = [
    { title: "#", dataIndex: "id", width: 110, render: (v: number) => <span className="ui-num">{v}</span> },
    {
      title: "依頼先",
      width: 240,
      render: (_: unknown, e) => (
        <div>
          <div>{e.partner_name}</div>
          <div className="ui-cell-sub">{e.partner_email || "宛先なし"}</div>
        </div>
      ),
    },
    { title: "向き", dataIndex: "action_label", width: 160 },
    { title: "依頼項目", width: 320, render: (_: unknown, e) => e.items.join(" / ") },
    { title: "状態", width: 200, render: (_: unknown, e) => externalStatusTag(e) },
    { title: "送信", width: 180, render: (_: unknown, e) => jst(e.sent_at) },
    {
      title: "操作",
      width: 90,
      render: (_: unknown, e) => (
        <Link to={`/hr/requests?panel=${e.id}`}>
          <Button size="small">文面</Button>
        </Link>
      ),
    },
  ];

  return (
    <DetailPage
      back={{ to: "/hr/requests/list", label: "申請一覧へ" }}
      title={`申請 #${req.id} ${kindLabel(req.kind)}`}
      tags={
        <Space size={4}>
          {requestStatusTag(req)}
          <SlaTag sla={req.sla} />
        </Space>
      }
      sub={
        <>
          <span>
            <Link to={`/hr/employees/${req.employee_id}`}>{req.employee_name}</Link>{" "}
            <Tag>{statusLabel(req.employee_status)}</Tag>
          </span>
          <span>
            申請 {req.requested_by} · {jst(req.created_at)}
          </span>
        </>
      }
      actions={
        <Space>
          <ApproveReject req={req} size="middle" />
          <Withdraw key={req.id} req={req} size="middle" onDone={() => navigate("/hr/requests/list")} />
          <DeleteClosed req={req} size="middle" onDone={() => navigate("/hr/requests/list")} />
        </Space>
      }
      alert={
        req.status === "rejected" && req.reason ? (
          <Alert type="warning" showIcon message={`却下: ${req.reason}`} />
        ) : req.status === "withdrawn" ? (
          <Alert type="info" showIcon message={`取り下げ${req.reason ? `: ${req.reason}` : ""}`} />
        ) : undefined
      }
      summary={[
        { label: "対象者", value: req.employee_name, href: `/hr/employees/${req.employee_id}` },
        { label: "状態", value: requestStatusLabel(req.status), tone: req.status === "rejected" ? "danger" : undefined },
        { label: "承認", value: req.approvals },
        { label: "テンプレート", value: req.template_name, empty: "なし" },
        { label: "日付", value: req.start_date, empty: "未設定" },
        { label: "タスク", value: req.task_total ? `${req.task_done} / ${req.task_total}` : "", empty: "未起票" },
      ]}
      tabs={[
        {
          key: "overview",
          label: "概要",
          children: (
            <>
              <Section title="申請の内容(申請時のスナップショット)">
                <KeyValueList columns={2} items={contentItems} />
              </Section>
              <Section title="申請">
                <KeyValueList
                  columns={3}
                  items={[
                    { label: "申請者", value: req.requested_by },
                    { label: "承認者", value: req.approver ? `${req.approver.label}: ${req.approver.names.join("、")}` : "指定なし" },
                    { label: "申請日時", value: jst(req.created_at) },
                    { label: "決着", value: req.decided_at ? `${req.decided_by} / ${jst(req.decided_at)}` : "" },
                  ]}
                />
              </Section>
            </>
          ),
        },
        {
          key: "tasks",
          label: "タスク",
          badge: req.tasks.length,
          children: (
            <Section title="タスク" flush>
              <Table<WfTask>
                rowKey="id"
                size="small"
                columns={taskColumns}
                dataSource={req.tasks}
                pagination={false}
                scroll={{ x: true }}
                locale={{ emptyText: req.status === "pending" ? "承認が揃うと起票する" : "タスクはない" }}
              />
            </Section>
          ),
        },
        {
          key: "external",
          label: "社外依頼",
          badge: req.external_requests.length,
          children: (
            <Section title="社外依頼" flush>
              <Table<WfExternalRequest>
                rowKey="id"
                size="small"
                columns={extColumns}
                dataSource={req.external_requests}
                pagination={false}
                scroll={{ x: true }}
                locale={{ emptyText: "社外依頼はない" }}
              />
            </Section>
          ),
        },
        {
          key: "history",
          label: "履歴",
          children: (
            <>
              <Section title="承認ルート">
                <Steps items={approvalRoute(req)} />
              </Section>
              <Section title="判断の記録">
                {req.decisions.length === 0 ? (
                  <Typography.Text type="secondary">まだ誰も判断していない</Typography.Text>
                ) : (
                  <Space direction="vertical">
                    {req.decisions.map((d) => (
                      <span key={d.id}>
                        <Tag color={d.decision === "approve" ? "green" : "red"}>
                          {d.decision === "approve" ? "承認" : "却下"}
                        </Tag>
                        {d.decided_by} / {jst(d.created_at)} {d.reason}
                      </span>
                    ))}
                  </Space>
                )}
              </Section>
            </>
          ),
        },
      ]}
    />
  );
}
